import React from 'react';
import {isAxiosError} from 'axios';
import {useNavigate} from 'react-router-dom';
import {ConfirmationModal} from '@rocket-ui/components';
import {useCreatePaymentWay, useFetchPaymentWays} from '@rocket-ui/lk-api';
import {ROUTES} from 'app/router/routes';
import {
	ACCOUNT_PAYMENT_SUCCESS_DESCRIPTION,
	ACCOUNT_PAYMENT_SUCCESS_TITLE,
	AccountBikStep,
	AccountNumberStep,
	AccountReviewStep,
	ProfileAlertsBanner,
	StepProgress,
	isAccountNumberValid,
	buildAccountPaymentWayRequest,
	useProfile,
} from 'features/profile';
import type {SelectedBank} from 'features/profile';
import {RegistrationButton} from 'features/registration';
import styles from './AddAccountPaymentPage.module.css';

type AccountStep = 'bik' | 'account' | 'review';

const STEPS: AccountStep[] = ['bik', 'account', 'review'];

const backIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<path d="M10 6L4 12L10 18" stroke="currentColor" strokeWidth="1.6" />
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M20 12.9135L5 12.9135L5 11.1992L20 11.1992L20 12.9135Z"
			fill="currentColor"
		/>
	</svg>
);

const closeIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<path d="M3.99805 3.99805L20.0004 20.0004" stroke="currentColor" strokeWidth="1.6" />
		<path d="M3.99805 20L20.0004 3.99768" stroke="currentColor" strokeWidth="1.6" />
	</svg>
);

function getCreatePaymentWayErrorMessage(error: unknown): string {
	if (isAxiosError<{errors?: string[]; message?: string; detail?: string}>(error)) {
		return (
			error.response?.data?.errors?.[0] ||
			error.response?.data?.message ||
			error.response?.data?.detail ||
			error.message
		);
	}

	return error instanceof Error ? error.message : 'Не удалось добавить способ оплаты';
}

function formatRecipientName(profile: {
	secondName: string;
	firstName: string;
	patronymic: string;
}): string {
	return [profile.secondName, profile.firstName, profile.patronymic]
		.filter((part) => part && part !== '—')
		.join(' ');
}

export function AddAccountPaymentPage(): React.ReactElement {
	const navigate = useNavigate();
	const {profile} = useProfile();
	const {data: paymentWays = []} = useFetchPaymentWays();
	const {mutateAsync: createPaymentWay, isLoading: isCreating} = useCreatePaymentWay();

	const [step, setStep] = React.useState<AccountStep>('bik');
	const [bik, setBik] = React.useState('');
	const [account, setAccount] = React.useState('');
	const [selectedBank, setSelectedBank] = React.useState<SelectedBank | null>(null);
	const [isPrimary, setIsPrimary] = React.useState(false);
	const [submitError, setSubmitError] = React.useState<string | null>(null);
	const [isExitConfirmOpen, setIsExitConfirmOpen] = React.useState(false);
	const [isSuccessOpen, setIsSuccessOpen] = React.useState(false);

	const hasExistingPaymentWays = paymentWays.some((way) => way.visible !== false);
	const showPrimaryToggle = hasExistingPaymentWays;
	const recipient = formatRecipientName(profile) || '—';

	const stepIndex = STEPS.indexOf(step);
	const canContinueBik = Boolean(selectedBank);
	const canContinueAccount = isAccountNumberValid(account, bik);
	const isContinueDisabled =
		isCreating ||
		isSuccessOpen ||
		(step === 'bik' && !canContinueBik) ||
		(step === 'account' && !canContinueAccount);
	const showContinue = step !== 'review' && (step !== 'bik' || canContinueBik);
	const showBack = step !== 'bik';

	React.useEffect(() => {
		if (!hasExistingPaymentWays) {
			setIsPrimary(true);
		}
	}, [hasExistingPaymentWays]);

	const handleCloseRequest = () => {
		if (isCreating || isSuccessOpen) {
			return;
		}

		setIsExitConfirmOpen(true);
	};

	const handleConfirmExit = () => {
		setIsExitConfirmOpen(false);
		navigate(ROUTES.profile);
	};

	const handleSuccessContinue = () => {
		setIsSuccessOpen(false);
		navigate(ROUTES.profile);
	};

	const handleBack = () => {
		if (isCreating || isSuccessOpen) {
			return;
		}

		if (step === 'review') {
			setStep('account');
			setSubmitError(null);
			return;
		}

		if (step === 'account') {
			setStep('bik');
		}
	};

	const handleContinue = async () => {
		setSubmitError(null);

		if (step === 'bik') {
			if (!canContinueBik) {
				return;
			}

			setStep('account');
			return;
		}

		if (step === 'account') {
			if (!canContinueAccount) {
				return;
			}

			setStep('review');
			return;
		}

		if (isCreating) {
			return;
		}

		const request = buildAccountPaymentWayRequest({
			bik,
			account,
			selectedBank,
			isPrimary: hasExistingPaymentWays ? isPrimary : true,
		});

		if (!request) {
			setSubmitError('Проверьте данные счёта');
			return;
		}

		try {
			await createPaymentWay(request);
			setIsSuccessOpen(true);
		} catch (error) {
			setSubmitError(getCreatePaymentWayErrorMessage(error));
		}
	};

	return (
		<div className={styles.root}>
			<header className={styles.header}>
				<div className={styles.headerInner}>
					{showBack ? (
						<button type="button" className={styles.backButton} onClick={handleBack} aria-label="Назад">
							{backIcon}
						</button>
					) : (
						<span className={styles.headerSpacer} aria-hidden />
					)}
					<StepProgress currentStep={stepIndex} totalSteps={STEPS.length} className={styles.progress} />
					<button
						type="button"
						className={styles.closeButton}
						onClick={handleCloseRequest}
						aria-label="Закрыть"
					>
						{closeIcon}
					</button>
				</div>
			</header>

			<div className={styles.content}>
				<div className={`${styles.contentInner}${step === 'review' ? ` ${styles.contentInnerWhite}` : ''}`}>
					<ProfileAlertsBanner />
					{step === 'bik' ? (
						<AccountBikStep
							value={bik}
							selectedBank={selectedBank}
							onChange={setBik}
							onBankSelect={setSelectedBank}
						/>
					) : null}

					{step === 'account' ? (
						<AccountNumberStep value={account} bik={bik} onChange={setAccount} />
					) : null}

					{step === 'review' ? (
						<>
							<AccountReviewStep
								recipient={recipient}
								bik={bik}
								account={account}
								bank={selectedBank}
								isPrimary={isPrimary}
								showPrimaryToggle={showPrimaryToggle}
								onPrimaryChange={setIsPrimary}
								error={submitError}
							/>
							<div className={styles.contentActions}>
								<RegistrationButton
									variant="primary"
									className={styles.continueButton}
									onClick={handleContinue}
									disabled={isContinueDisabled}
									loading={isCreating}
								>
									Всё верно
								</RegistrationButton>
							</div>
						</>
					) : null}
				</div>
			</div>

			{showContinue ? (
				<footer className={styles.footer}>
					<div className={styles.footerInner}>
						<RegistrationButton
							variant="primary"
							className={styles.continueButton}
							onClick={handleContinue}
							disabled={isContinueDisabled}
							loading={isCreating}
						>
							Продолжить
						</RegistrationButton>
					</div>
				</footer>
			) : null}

			<ConfirmationModal
				isOpen={isExitConfirmOpen}
				title="Хотите выйти?"
				description="Вы уверены, что хотите прервать добавление реквизитов?"
				confirmText="Выйти"
				onConfirm={handleConfirmExit}
				onCancel={() => setIsExitConfirmOpen(false)}
			/>

			<ConfirmationModal
				isOpen={isSuccessOpen}
				title={ACCOUNT_PAYMENT_SUCCESS_TITLE}
				description={ACCOUNT_PAYMENT_SUCCESS_DESCRIPTION}
				confirmText="Продолжить"
				onConfirm={handleSuccessContinue}
				onCancel={handleSuccessContinue}
			/>
		</div>
	);
}
